import { css } from '@/styled-system/css';

/**
 * Inline "Verified" indicator (shield icon + tooltip-triggering label) that
 * sits at the end of a specs line — shared by `ListingCard`'s beds/baths/sqft
 * row and the HDP's `ListingSummaryModule` meta row, so both surfaces render
 * the same composition instead of the HDP overlaying a separate pill badge.
 */
export const verifiedInline = css({
  alignItems: 'center',
  display: 'inline-flex',
  flexShrink: 0,
  fontSize: 'body-sm',
  gap: 'micro.default',
  // Reset the inherited line-height so the inline-flex box collapses to its
  // content height (icon + text = 1em).
  // eslint-disable-next-line hotpads/no-magic-numbers -- unitless 1 = font-size; no Panda token exists for this CSS value
  lineHeight: 1,
  verticalAlign: 'middle',
  whiteSpace: 'nowrap',
  // The TriggerText button renders as a ~15px-tall text line sitting on top
  // of the card's full-surface link, so neighbor spacing can never satisfy
  // WCAG 2.5.8 (24px minimum target). Grow the button's hit box upward to
  // 24px — bottom-aligned so the dashed trigger underline (a borderBlockEnd
  // on the button) keeps hugging the text — and pull the flow back with a
  // negative top margin so the specs line keeps its single-line height. The
  // 24px in minHeight and in the margin calc must move together.
  '& button': {
    display: 'inline-flex',
    alignItems: 'flex-end',
    // eslint-disable-next-line hotpads/no-magic-numbers, constellation/no-raw-spacing-values -- WCAG 2.5.8 minimum tap-target size
    minHeight: '24px',

    marginTop: 'calc(1em - 24px)',
  },
});

export const verifiedPhotoBadgeTrigger = css.raw({
  // Bottom-aligned so the visible pill hugs the photo footer's bottom inset;
  // the 44px tap area then extends upward over the photo instead of floating
  // the pill 12px above the footer edge. Block-level `flex` (not inline-flex)
  // so the trigger doesn't sit on the footer's text baseline, which would
  // reserve descender space below the pill.
  alignItems: 'flex-end',
  appearance: 'none',
  backgroundColor: 'bg.invisible',
  borderRadius: 'pill',
  borderWidth: 'none',
  color: 'inherit',
  cursor: 'pointer',
  display: 'flex',
  focusOutline: true,
  justifyContent: 'center',
  minBlockSize: 'tapArea.mobile.default',
  minInlineSize: 'tapArea.mobile.default',
  padding: 'none',
});

export const verifiedPhotoBadgeContent = css({
  alignItems: 'center',
  display: 'flex',
  gap: 'micro.default',
});
